import { find, isFunction, isString } from 'lodash'

import type { App, Component, VNode } from 'vue';
import { shallowReactive } from 'vue';

import { uuid } from '@wittignl/js-core'

import Overlay from '../component/overlay/Overlay';

declare module '@vue/runtime-core' {
    interface ComponentCustomProperties {
        $overlay: OverlayPlugin
    }
}

export interface OverlayInstance {
    component: Component
    props: Record<string, any>
    resolve: (value: OverlayResult) => void
    showDate: Date
    id: string
    options: OverlayOptions
    $overlayRef?: () => any
    $layerRef?: () => any
}

export interface OverlayOptions {
    id: string
    layer?: string|Component
    children?: Component[]
    shader?: boolean
    [key: string]: any
}

export interface OverlayResult {
    result: any
    overlay: OverlayInstance
}

export class OverlayPlugin {

    data = shallowReactive({
        overlays: shallowReactive([])
    } as { overlays: OverlayInstance[] })

    get overlays(): OverlayInstance[] {

        return this.data.overlays;
    }

    set overlays(overlays: OverlayInstance[]){

        this.data.overlays = overlays;
    }

    show(component: Component, props: Record<string, any> = {}, optionsOrId: string|OverlayOptions): Promise<OverlayResult> {

        const options: OverlayOptions = isString(optionsOrId) ? { id: optionsOrId } : optionsOrId;
        const id = options?.id || uuid();

        if(this.isOpen(id)){
            return Promise.reject(`Overlay with ${id} is already visible`);
        }

        const showDate = new Date();

        return new Promise((resolve) => {

            // After timeout te prevent document click bubble
            this.delayed(() => {

                this.overlays.push({
                    component,
                    props,
                    resolve,
                    options,
                    showDate,
                    id
                });
            });
        });
    }

    updateOptions(id: string, options: OverlayOptions){

        const overlay = this.findOverlay(id);
        if(overlay){
            overlay.options = { ...overlay.options, ...options };
        }
    }

    close(overlayOrId: OverlayInstance|string): Promise<void> {

        const overlay = isString(overlayOrId) ? this.findOverlay(overlayOrId) : overlayOrId;
        if(!overlay){
            return Promise.resolve();
        }

        let promises = [Promise.resolve()];

        const layerInstance = overlay.$layerRef();

        if(layerInstance && isFunction(layerInstance['closeOverlay'])){

            const closeResult = layerInstance['closeOverlay']();

            if(closeResult instanceof Promise){
                promises.push(closeResult);
            }
        }

        const componentInstance = overlay.$overlayRef();

        if(componentInstance && isFunction(componentInstance['closeOverlay'])){

            const closeResult = componentInstance['closeOverlay']();

            if(closeResult instanceof Promise){
                promises.push(closeResult);
            }
        }

        return new Promise(resolve => {

            Promise.all(promises).then(() => this.delayed(() => {

                const index = this.overlays.indexOf(overlay);
                if(index < 0){
                    return;
                }

                this.overlays.splice(index, 1);
                this.delayed(resolve);
            }));
        });
    }

    dismiss(overlay: OverlayInstance, resultData: any){

        this.close(overlay);
        this.resolve(overlay, resultData);
    }

    resolve(overlay: OverlayInstance, resultData: any){

        overlay.resolve({
            result: resultData,
            overlay
        });
    }

    cancelTopOverlay(event = null){

        const topOverlay = this.topOverlay;
        if(!topOverlay || !topOverlay.$overlayRef){
            return;
        }

        if(topOverlay.options.beforeCancel){

            const result = topOverlay.options.beforeCancel(event);
            if(result === false){
                return;
            }
        }

        const componentInstance = topOverlay.$overlayRef();

        if(isFunction(componentInstance['cancel'])){
            componentInstance['cancel']();
        }
    }

    delayed(func: () => any){

        setTimeout(func, 1);
    }

    findOverlay(id: string): OverlayInstance {

        return find(this.overlays, { id });
    }

    isOpen(id: string): boolean {

        return !!this.findOverlay(id);
    }

    get topOverlay(): OverlayInstance {

        return this.overlays.length ? this.overlays[this.overlays.length - 1] : null;
    }

    install(app: App){

        app.config.globalProperties.$overlay = this;

        app.component('ui-overlay-container', Overlay);
    }
}

export default OverlayPlugin;